import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";

import { Flex, Stack, Table, Link as ChakraLink } from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "@/codegen/ui/button";
import { Territories } from "@/components/2/geo";
import { JobName } from "@/components/2/hohishes";
import { useGetEnrichedHohishPostings } from "@/services/2/hohishes/queries";
import { EnrichedHohishPostingType } from "@/services/2/hohishes/types";

import { useEnrichedHohishOutletContext } from "../local-context";

const columnHelper = createColumnHelper<EnrichedHohishPostingType>();

const dateFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const formatDate = (date: string) => dateFormatter.format(new Date(`${date}T12:00:00`));
const groups = [
  { id: "current", label: "En cours" },
  { id: "future", label: "À venir" },
  { id: "past", label: "Terminées" },
] as const;
const status = (posting: EnrichedHohishPostingType, today: string) =>
  posting.starts_on > today
    ? "future"
    : posting.ends_on && posting.ends_on < today
      ? "past"
      : "current";

const columns = [
  columnHelper.accessor("job", {
    header: "Poste",
    cell: ({ cell }) => <JobName job={cell.getValue()} />,
    size: 220,
  }),
  columnHelper.accessor("type", {
    header: "Type",
    cell: ({ cell }) => (cell.getValue() === "permanent" ? "Permanente" : "Backup"),
    size: 140,
  }),
  columnHelper.accessor("starts_on", {
    header: "Premier jour",
    cell: ({ cell }) => formatDate(cell.getValue()),
    minSize: 120,
    size: 120,
  }),
  columnHelper.accessor("ends_on", {
    header: "Dernier jour",
    cell: ({ cell }) => (cell.getValue() ? formatDate(cell.getValue()!) : "Sans fin prévue"),
    minSize: 120,
    size: 120,
  }),
  columnHelper.accessor("territoryUuids", {
    header: "Territoire(s)",
    cell: ({ cell }) => <Territories territoryUuids={cell.getValue()} />,
  }),
  columnHelper.display({
    header: "Actions",
    cell: ({ row }) => (
      <RouterLink to={`${row.original.uuid}/edit`} style={{ textDecoration: "underline" }}>
        <ChakraLink variant="underline" colorPalette={"blue"} as="span">
          Modifier
        </ChakraLink>
      </RouterLink>
    ),
    size: 100,
  }),
];

export const PostingsHohish = () => {
  const hohish = useEnrichedHohishOutletContext();
  const navigate = useNavigate();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const { data, isPending, isPlaceholderData, isError } = useGetEnrichedHohishPostings(
    { path: { hohish_uuid: hohish.uuid } },
    ["uncached"],
    {
      placeholderData: [],
    }
  );
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: posting => posting.uuid,
  });
  const rows = table.getRowModel().rows;
  return (
    <Stack gap={4}>
      <Flex justify="flex-end" gap={4}>
        <Button onClick={() => navigate("create")}>Nouvelle affectation</Button>
      </Flex>
      {isError ? (
        <p>Impossible de charger les affectations.</p>
      ) : isPending || isPlaceholderData ? (
        <p>Chargement des affectations…</p>
      ) : (
        <Table.ScrollArea borderWidth="1px" rounded="md">
          <Table.Root size="sm" tableLayout="fixed" minWidth="960px">
            <colgroup>
              {table.getAllLeafColumns().map(column => (
                <col
                  key={column.id}
                  style={{ width: column.id === "territoryUuids" ? undefined : column.getSize() }}
                />
              ))}
            </colgroup>
            <Table.Header>
              {table.getHeaderGroups().map(group => (
                <Table.Row key={group.id}>
                  {group.headers.map(header => (
                    <Table.ColumnHeader key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              ))}
            </Table.Header>
            {groups.map(group => {
              const groupRows = rows
                .filter(row => status(row.original, today) === group.id)
                .sort((a, b) => {
                  const left = a.original;
                  const right = b.original;
                  return (
                    (group.id === "past"
                      ? right.ends_on!.localeCompare(left.ends_on!)
                      : group.id === "future"
                        ? left.starts_on.localeCompare(right.starts_on)
                        : right.starts_on.localeCompare(left.starts_on)) ||
                    left.uuid.localeCompare(right.uuid)
                  );
                });
              const collapsible = group.id === "past" && groupRows.length > 5;
              const expanded = !collapsible || historyExpanded;
              return (
                <Table.Body key={group.id} id={group.id === "past" ? "past-postings" : undefined}>
                  <Table.Row bg={group.id === "current" ? "blue.subtle" : "bg.muted"}>
                    <Table.Cell colSpan={columns.length} fontWeight="semibold">
                      <Flex align="center" gap={3}>
                        <span>
                          {group.label} · {groupRows.length}
                        </span>
                        {collapsible && (
                          <ChakraLink
                            as="button"
                            type="button"
                            variant="underline"
                            colorPalette="blue"
                            fontWeight="normal"
                            onClick={() => setHistoryExpanded(previous => !previous)}
                            aria-expanded={expanded}
                            aria-controls="past-postings"
                          >
                            {expanded ? "Voir moins" : "Voir plus"}
                          </ChakraLink>
                        )}
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                  {groupRows.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={columns.length} color="fg.muted">
                        {group.id === "current"
                          ? "Aucune affectation en cours"
                          : group.id === "future"
                            ? "Aucune affectation à venir"
                            : "Aucune affectation terminée"}
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {expanded &&
                    groupRows.map(row => (
                      <Table.Row key={row.id} color={group.id === "past" ? "fg.muted" : undefined}>
                        {row.getVisibleCells().map(cell => (
                          <Table.Cell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                </Table.Body>
              );
            })}
          </Table.Root>
        </Table.ScrollArea>
      )}
    </Stack>
  );
};
